import { Navigate } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import { RedirectToLogin } from './LoginModal';

// Preferred landing per role (used when the user can actually access it).
const ROLE_HOME: Record<string, string> = {
  SUPER_ADMIN: '/dashboard',
  ADMIN: '/dashboard',
  ACCOUNTS: '/billing/aging',
  SALES_HEAD: '/dashboard',
  SALES_PERSON: '/dashboard',
  WAREHOUSE_MANAGER: '/warehouse/dashboard',
  RETAILER: '/retailer/dashboard',
  // In-app home only (RoleHome redirects INTO the app). '/catalog' is a PUBLIC route and isn't a
  // CANDIDATE, so it silently fell through to /complaints — point farmers at their profile instead.
  FARMER: '/profile',
};

// Fallback chain: the first landing the user's permissions allow wins. Each `anyOf`
// MUST mirror that route's guard in App.tsx. The list ends at '/profile' (auth-only,
// no permission), so RoleHome can NEVER redirect anyone to a page they'll 403 on —
// even a future role or a mis-mapped one.
const CANDIDATES: { path: string; anyOf?: string[] }[] = [
  { path: '/dashboard', anyOf: ['DASH.EXECUTIVE', 'DASH.SALES'] },
  { path: '/warehouse/dashboard', anyOf: ['WAREHOUSE.VIEW.ASSIGNED'] },
  // Retailer home. Guarded on OUTSTANDING.VIEW (mirrors the route), no denyRoles → never 403s. Placed
  // before /billing/aging (which denies CUSTOMER_ROLES) so a retailer fallback lands here, not a 403.
  { path: '/retailer/dashboard', anyOf: ['OUTSTANDING.VIEW'] },
  { path: '/billing/aging', anyOf: ['OUTSTANDING.VIEW'] },
  { path: '/orders', anyOf: ['ORDER.VIEW'] },
  { path: '/ffa/my-day', anyOf: ['ATTENDANCE.SELF', 'VISIT.CREATE'] },
  { path: '/complaints', anyOf: ['COMPLAINT.VIEW'] },
  // Last resort = an IN-APP page (auth-only, always reachable). Never fall back to a PUBLIC route
  // like /catalog from here: RoleHome redirects INTO the app, and landing on a public page makes the
  // "Dashboard" button look broken (it dumps you back on the marketing site you came from).
  { path: '/profile' },
];

export function RoleHome() {
  const user = useAuthStore((s) => s.user);
  const hasAny = useAuthStore((s) => s.hasAnyPermission);
  if (!user) return <RedirectToLogin />;

  // 1) Prefer the role's natural home — but only if the user can actually reach it.
  const preferred = CANDIDATES.find((c) => c.path === ROLE_HOME[user.role]);
  if (preferred && hasAny(preferred.anyOf)) {
    return <Navigate to={preferred.path} replace />;
  }
  // 2) Otherwise the first landing their permissions allow (guaranteed: /profile).
  const target = CANDIDATES.find((c) => hasAny(c.anyOf)) ?? { path: '/profile' };
  return <Navigate to={target.path} replace />;
}
